import { useState, useEffect, useContext, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import "../InvoicesManagement.scss";
import useLanguage from "../../utils/useLanguage";
import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import CustomButton from "../../components/CustomButton";
import InformationBox from "../../components/InformationBox";
import BackToDashboard from "../../components/BackToDashboard";
import Loader from "../../components/Loader";
import RoundedTable from "../../components/table-component/RoundedTable";
import { TABLE_CELL_TYPES } from "../../components/table-component/RoundedTable";
import AddDiscountModal from "./AddDiscountModal";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import addIconLight from "../../assets/add_icon_light.png";
import searchIcon from "../../assets/search_icon.png";
import infoIcon from "../../assets/information.png";

import {
  getAllDiscounts,
  deleteDiscount,
} from "../../services/schoolDiscountsServices";
import DiscountInfoModal from "./DiscountInfoModal";

const DiscountsPage = forwardRef((props, ref) => {
  // Context and hooks
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  const [loading, setLoading] = useState(false);

  // State variables
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("name"),
    value: "name",
  });
  const [selectedType, setSelectedType] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [showDiscountModal, setShowAddDiscount] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // Constants
  const tableTitles = [
    { value: "name", keepOnMobile: true },
    { value: "discountType", keepOnMobile: true },
    { value: "value", keepOnMobile: true },
    { value: "theOptions", keepOnMobile: true },
  ];

  const titles = [
    { title: t("name"), value: "name" },
    { title: t("discountType"), value: "discountType" },
  ];

  const discountTypes = [
    { title: t("byPercentage"), value: true },
    { title: t("byAmount"), value: false },
  ];

  // Handlers
  const handleViewInfoClick = (discount) => {
    setSelectedDiscount(discount);
    setShowInfoModal(true);
  };

  // Data fetching
  const getAllDiscountsData = async (page, searchQuery) => {
    try {
      setLoading(true);
      const response = await getAllDiscounts({
        page: pagination.currentPage,
        limit: 5,
        search: selectedTitle.value === "discountType" ? null : searchQuery,
        isPercentage:
          selectedTitle.value === "discountType" ? searchQuery : null,
      });

      const discounts = response.data.discounts;
      const formattedDiscounts = discounts.map((discount) => [
        { value: discount.name, type: TABLE_CELL_TYPES.TEXT,
          keepOnMobile: true
         },
        {
          value: discount.isPercentage ? t("byPercentage") : t("byAmount"),
          type: TABLE_CELL_TYPES.TEXT,
          keepOnMobile: true
        },
        {
          value: discount.isPercentage
            ? `${discount.amount} %`
            : `${discount.amount} ${t("IQD")}`,
          type: TABLE_CELL_TYPES.TEXT,
          currency: discount.amount ? `${t("IQD")}` : "",
          keepOnMobile: true
        },
        {
          value: (
            <CustomButton
              variant="blank"
              title={t("ViewInfo")}
              onClick={() => handleViewInfoClick(discount)}
              icon={infoIcon}
              iconSize={15}
            />
          ),
          type: TABLE_CELL_TYPES.ELEMENT,
          keepOnMobile: true
        },
      ]);

      setDiscounts(formattedDiscounts);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response?.data?.totalPages,
        totalRecords: response?.data?.totalRecords,
      });
    } catch (error) {
      console.error("Error fetching discounts:", error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    getAllDiscountsData(pagination.currentPage, searchQuery);
  }, [pagination.currentPage]);

  return (
    <>
      <main className="e-container">
        {/*<BackToDashboard*/}
        {/*  title={t("back")}*/}
        {/*  variant="blank"*/}
        {/*  icon={currentDirection === "ltr" ? previousIcon : nextIcon}*/}
        {/*  iconSize={14}*/}
        {/*  to="/students"*/}
        {/*/>*/}
        <div className="invoice-table-container">
          <div className="under-user-section">
            <div className="page-title">{t("discounts")}</div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="u-margin--bottom_300">
                <div className=" o-row o-row--horizontal-align_end">
                  <CustomButton
                    title={t("addDiscount")}
                    onClick={() => setShowAddDiscount(true)}
                    icon={addIconLight}
                    iconSize={20}
                  />
                </div>
              </div>

              <div className="c-search-bar u-margin--bottom_500 u-margin--top_400">
                <section className="c-search-bar-label-input">
                  {selectedTitle.value === "name" ? (
                    <>
                      <label>{t("search")}:</label>
                      <CustomInput
                        placeholder={t("search")}
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setPagination((prev) => ({
                            ...prev,
                            currentPage: 1,
                          }));
                        }}
                        resetSearch={() => {
                          setSearchQuery("");
                          setPagination((prev) => ({
                            ...prev,
                            currentPage: 1,
                          }));
                        }}
                        thick
                      />
                    </>
                  ) : (
                    <>
                      <label>{t("search")}:</label>
                      <Dropdown
                        options={discountTypes}
                        selected={selectedType}
                        setSelected={(newTitle) => {
                          setSelectedType(newTitle);
                          setSearchQuery(newTitle ? newTitle.value : "");
                          setPagination((prev) => ({
                            ...prev,
                            currentPage: 1,
                          }));
                        }}
                        flex
                        thick
                      />
                    </>
                  )}
                </section>

                <section className="c-search-bar-label-input">
                  <label>{t("searchTitle")}: </label>
                  <Dropdown
                    options={titles}
                    selected={selectedTitle}
                    setSelected={(newTitle) => {
                      setSelectedTitle(newTitle);
                      setSearchQuery("");
                    }}
                    thick
                  />
                </section>
                <CustomButton
                  title={t("search")}
                  icon={searchIcon}
                  iconSize={15}
                  onClick={() => getAllDiscountsData(1, searchQuery)}
                />
              </div>

              {discounts.length > 0 ? (
                <RoundedTable
                  titles={tableTitles}
                  rows={discounts}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              ) : (
                <InformationBox>{t("noDiscountsFound")}</InformationBox>
              )}
            </>
          )}
        </div>
      </main>

      {showDiscountModal && (
        <AddDiscountModal
          setShowAddDiscountModal={setShowAddDiscount}
          getAllDiscounts={getAllDiscountsData}
        />
      )}

      {showInfoModal && selectedDiscount && (
        <DiscountInfoModal
          discount={selectedDiscount}
          onClose={() => setShowInfoModal(false)}
          getAllDiscounts={getAllDiscountsData}
        />
      )}
    </>
  );
});

export default DiscountsPage;
